import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Stack from "@mui/material/Stack";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { grey } from "@mui/material/colors";
import { createFilterOptions } from "@mui/material";

const LISTBOX_MAX_HEIGHT = 280;
const NAME_SELECT_LIMIT = 5;

const filterOptions = createFilterOptions({
	matchFrom: "any",
	stringify: (option) => `${option?.label ?? ""} ${option?.key ?? ""}`,
});

const TotalInTotalOutNameSelect = ({ column, data, setData }) => {
	const optionsList = (column?.select_options?.list || []).map((row) => ({
		...row,
		label: row?.name ?? row?.label,
	}));
	const selectedValue = Array.isArray(data?.[column.key]) ? data[column.key] : [];
	const selectedOptions = optionsList.filter((opt) => selectedValue.includes(opt.key));
	const atLimit = selectedValue.length >= NAME_SELECT_LIMIT;

	return (
		<Stack sx={{ width: "100%", minWidth: 0 }} spacing={0.5} minHeight={72}>
			<InputLabel
				sx={{
					overflow: "hidden",
					textOverflow: "ellipsis",
					whiteSpace: "nowrap",
					color: grey[700],
					fontSize: "0.75rem",
					fontWeight: 600,
					letterSpacing: "0.02em",
					textTransform: "uppercase",
				}}>
				{column?.name}
				<span style={{ color: "#d32f2f" }}> *</span>
			</InputLabel>
			<Autocomplete
				multiple
				disableCloseOnSelect
				fullWidth
				size="small"
				options={optionsList}
				filterOptions={filterOptions}
				getOptionLabel={(option) => option?.label ?? ""}
				getOptionDisabled={(option) => atLimit && !selectedValue.includes(option.key)}
				value={selectedOptions}
				isOptionEqualToValue={(opt, val) => opt?.key === val?.key}
				onChange={(_, value) => {
					const nextKeys = value.map((item) => item.key).slice(0, NAME_SELECT_LIMIT);
					setData({ ...data, [column.key]: nextKeys });
				}}
				ListboxProps={{
					style: {
						maxHeight: LISTBOX_MAX_HEIGHT,
						overflow: "auto",
						paddingTop: 4,
						paddingBottom: 4,
					},
				}}
				renderTags={(value) => (
					<Chip
						size="small"
						label={`${value.length} selected`}
						sx={{
							height: 24,
							maxWidth: "70%",
							borderRadius: "6px",
							bgcolor: grey[100],
							fontWeight: 600,
							"& .MuiChip-label": {
								px: 1,
								overflow: "hidden",
								textOverflow: "ellipsis",
							},
						}}
					/>
				)}
				renderOption={(props, option, { selected }) => {
					const { key, ...optionProps } = props;
					return (
						<Box
							component="li"
							key={key}
							{...optionProps}
							sx={{
								px: "10px !important",
								py: "6px !important",
								mx: 0.5,
								my: 0.25,
								borderRadius: 1,
								gap: 0.5,
							}}>
							<Checkbox checked={selected} size="small" sx={{ margin: 0, padding: "2px", mr: 0.75 }} disableRipple />
							<Typography variant="body2" noWrap title={option.label}>
								{option.label}
							</Typography>
						</Box>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						placeholder={selectedValue.length ? "Search…" : "Search Name (max 5)"}
						variant="outlined"
					/>
				)}
				sx={{
					"& .MuiOutlinedInput-root": {
						minHeight: 40,
						borderRadius: 1.5,
						bgcolor: "grey.50",
						alignItems: "center",
						flexWrap: "nowrap",
						pr: 1,
						"&:hover": { bgcolor: "common.white" },
						"&.Mui-focused": {
							bgcolor: "common.white",
							boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.12)",
						},
					},
					"& .MuiAutocomplete-inputRoot": {
						display: "flex",
						alignItems: "center",
						flexWrap: "nowrap",
						overflow: "hidden",
						py: "2px !important",
					},
					"& .MuiAutocomplete-input": {
						minWidth: "48px !important",
						padding: "2px 0 !important",
					},
				}}
			/>
		</Stack>
	);
};

export default TotalInTotalOutNameSelect;
